import React, { useState, useEffect } from 'react';
import { icons, dropdowns, layouts, switches, textblocks } from 'seal-ui-components';
import styled from 'styled-components';
import mixpanelEvents from '@Mixpanel';
import { guid, getCurrentPage } from '@Utils/commonUtils';
import { FavoriteButton } from '@Components/UI/FavoriteButton';
import style, { LoadingOverLay, Subtitle } from './CustomSingleSelect.style';
import ReactTooltip from 'react-tooltip';

const { Value } = textblocks;
const { LoadingLargeIcon } = icons;
const { Select, SelectOption } = dropdowns;
const { HorizontalBox, VerticalBox, BoxSpacer } = layouts;

const id = guid();

const isExist = obj => !!Object.keys(obj).length;

const CustomSingleSelect = ({
    name,
    options,
    setOptions,
    className,
    defaultOpen,
    showFavorite,
    onFavoriteClick,
    togglingFavorites,
    ...rest
}) => {
    const [value, setValue] = useState('');
    const [favorites, setFavorites] = useState({});
    const [notFavorites, setNotFavorites] = useState({});

    useEffect(
        () => {
            Object.values(options).forEach(option => {
                if (option.selected) setValue(option.text);
            });

            let newFavorites = {};
            let newNotFavorites = {};

            Object.keys(options).forEach(key => {
                options[key].isFavorite
                    ? (newFavorites[key] = options[key])
                    : (newNotFavorites[key] = options[key]);
            });
            setFavorites(newFavorites);
            setNotFavorites(newNotFavorites);
        },
        [options]
    );

    const handleChange = key => {
        const tempItems = options;
        if (!options[key]) return;
        const { selected } = options[key];

        if (!selected) {
            Object.values(tempItems).forEach(item => {
                tempItems[key].text !== item.text
                    ? (item.selected = false)
                    : (tempItems[key].selected = !selected);
            });
        }

        setValue(key);
        setOptions({ ...tempItems });
    };

    const handleOpen = () => {
        const curDigApp = Object.values(options).filter(opt => opt.selected)[0];
        const digAppId = curDigApp ? curDigApp.id : '';

        const eventProperties = {
            'Analyzer Display Mode': getCurrentPage(),
            'Analyzer # Scorecard Id': digAppId
        };
        mixpanelEvents.scorecardSelectorItemSelected(eventProperties);
    };

    const valueItemGenerator = (key, index) => (
        <SelectOption key={`${name}_${index}_${guid()}`} value={key}>
            <HorizontalBox>
                <switches.Radio
                    id={`${name}_dropdown_label_${index}_value_${index}`}
                    checked={options[key].selected}
                    readOnly
                />
                <VerticalBox className="logic-app">
                    <HorizontalBox className="logic-app__name">
                        <div
                            data-tip={options[key].text || options[key].name}
                            data-for={`digapp-name`}
                        >
                            <Value data-testid={`${name}_dropdown_label_${index}`}>
                                {options[key].text || options[key].name}
                                {options[key].count && ` (${options[key].count})`}
                            </Value>
                        </div>
                    </HorizontalBox>
                    {options[key].description && (
                        <div data-tip={options[key].description} data-for={`digapp-desc`}>
                            <HorizontalBox className="logic-app__description">
                                <Value>{options[key].description}</Value>
                            </HorizontalBox>
                        </div>
                    )}
                </VerticalBox>

                <ReactTooltip
                    className="custom-tooltip"
                    id={`digapp-name`}
                    effect="solid"
                    multiline
                    place="top"
                />

                <ReactTooltip
                    className="custom-tooltip"
                    id={`digapp-desc`}
                    effect="solid"
                    multiline
                    place="bottom"
                />
                <BoxSpacer />
                {showFavorite && (
                    <FavoriteButton
                        logicAppName={key}
                        options={options}
                        onClick={onFavoriteClick}
                    />
                )}
            </HorizontalBox>
        </SelectOption>
    );

    const renderOptions = opts =>
        Object.keys(opts).map((key, index) => valueItemGenerator(key, index));

    return (
        <div className={className}>
            <Select
                key={`${name}_${id}`}
                displayedValue={value}
                placeholder={`Select ${name}`}
                appearance="secondary"
                hideClear
                onChange={handleChange}
                defaultOpened={defaultOpen}
                onOpen={handleOpen}
                {...rest}
            >
                {togglingFavorites && (
                    <LoadingOverLay>
                        <LoadingLargeIcon />
                    </LoadingOverLay>
                )}

                {isExist(favorites) && <Subtitle>Favorites</Subtitle>}
                {renderOptions(favorites)}

                {isExist(favorites) && isExist(notFavorites) && <Subtitle>All other</Subtitle>}
                {isExist(favorites) && renderOptions(notFavorites)}

                {!isExist(favorites) && renderOptions(options)}
            </Select>
        </div>
    );
};

export default styled(CustomSingleSelect)`
    ${style};
`;
